import React, { useCallback } from "react";

import FilterChip, { type FilterChipSize } from "../filter-chip/FilterChip";

/**
 * 複数選択可能なチップ群を描画するラッパー。
 *
 * 内部で `<FilterChip multi>` を並べるので、各チップには ✓ アイコンが
 * 表示され、「複数選べる」ことを視覚で明示する。
 *
 * 定型語彙（ショットタグ / ロールタグ）をクリックで付与する用途
 * （SubmitModal / ShotEditModal / RoleEditModal）で使う。
 *
 * `id` は `string | number` を受けるので、`ShotTag.id`(string)と
 * `RoleTag.id`(number)の両方をそのまま渡せる。
 */
export type TagSelectorId = string | number;

export interface TagSelectorItem<TId extends TagSelectorId = TagSelectorId> {
  id: TId;
  name: string;
}

export interface TagSelectorProps<TId extends TagSelectorId = TagSelectorId> {
  tags: TagSelectorItem<TId>[];
  selectedIds: TId[];
  // eslint-disable-next-line no-unused-vars
  onChange: (ids: TId[]) => void;
  /** チップサイズ。既定 "small"。 */
  size?: FilterChipSize;
  className?: string;
}

function TagSelector<TId extends TagSelectorId = TagSelectorId>({
  tags,
  selectedIds,
  onChange,
  size = "small",
  className,
}: TagSelectorProps<TId>): React.ReactElement | null {
  const handleToggle = useCallback(
    (tagId: TId) => {
      if (selectedIds.includes(tagId)) {
        onChange(selectedIds.filter((id) => id !== tagId));
      } else {
        onChange([...selectedIds, tagId]);
      }
    },
    [selectedIds, onChange],
  );

  if (!tags || tags.length === 0) return null;

  return (
    <div
      className={`u-row u-gap8 ${className ?? ""}`}
      style={{ flexWrap: "wrap" }}
    >
      {tags.map((tag) => (
        <FilterChip
          key={String(tag.id)}
          size={size}
          multi
          selected={selectedIds.includes(tag.id)}
          onClick={() => handleToggle(tag.id)}
        >
          {tag.name}
        </FilterChip>
      ))}
    </div>
  );
}

export default TagSelector;
